import React, { useState, useMemo } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import {
	PAGE_LAYOUT_FLAGS,
	PAGE_LAYOUT_MODE_OPTIONS,
	PAGE_LAYOUT_SURFACE_OPTIONS,
	type PageLayoutSettings,
} from "./pageLayoutSettings";

/**
 * Layout settings panel : controls how the public renderer composes the
 * page's <main> element. Standalone card so it sits as a sibling of the
 * metadata panel; the same component can later be reused inside the
 * content-type editor (page-template defaults).
 *
 * Controlled via {@link value} / {@link onChange}; the parent owns state.
 */
export type PageEditorLayoutPanelProps = {
	editable: boolean;
	value: PageLayoutSettings | null | undefined;
	onChange: (next: PageLayoutSettings | null) => void;
	/** Override the panel header subtitle (e.g., when used inside a content-
	 *  type editor as "default for new pages"). */
	subtitle?: string;
	/** Initial open/closed state. Defaults to closed : most pages don't
	 *  customize layout, so it stays out of the way. */
	defaultOpen?: boolean;
};

const PageEditorLayoutPanel: React.FC<PageEditorLayoutPanelProps> = ({
	editable,
	value,
	onChange,
	subtitle = "Controls the page shell : width, padding, and full-bleed flushes.",
	defaultOpen = false,
}) => {
	const [open, setOpen] = useState<boolean>(defaultOpen);

	/** Number of non-default settings : surfaced as a small badge on the
	 *  header so authors notice when a page deviates from defaults without
	 *  having to expand the card. */
	const customizedCount = useMemo(() => {
		if (!value) return 0;
		let n = 0;
		if (value.mode === "fluid" || value.mode === "constrained") n += 1;
		if (value.surface === "white" || value.surface === "tint") n += 1;
		if (value.firstWidgetMobileFlush) n += 1;
		if (value.lastWidgetFlushBottom) n += 1;
		return n;
	}, [value]);

	const modeValue = value?.mode ?? "";
	const surfaceValue = value?.surface ?? "";

	const handleModeChange = (raw: string) => {
		const next: PageLayoutSettings = { ...(value ?? {}) };
		if (raw === "fluid" || raw === "constrained") {
			next.mode = raw;
		} else {
			delete next.mode;
		}
		onChange(emptyToNull(next));
	};

	const handleSurfaceChange = (raw: string) => {
		const next: PageLayoutSettings = { ...(value ?? {}) };
		if (raw === "white" || raw === "tint") {
			next.surface = raw;
		} else {
			delete next.surface;
		}
		onChange(emptyToNull(next));
	};

	const handleFlagChange = (
		key: "firstWidgetMobileFlush" | "lastWidgetFlushBottom",
		checked: boolean
	) => {
		const next: PageLayoutSettings = { ...(value ?? {}) };
		if (checked) {
			next[key] = true;
		} else {
			delete next[key];
		}
		onChange(emptyToNull(next));
	};

	return (
		<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
			<div className="flex items-center justify-between gap-3">
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Layout</p>
						{customizedCount > 0 && (
							<span
								className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700"
								title={`${customizedCount} setting${customizedCount === 1 ? "" : "s"} differ from default`}
							>
								{customizedCount} customized
							</span>
						)}
					</div>
					<p className="text-sm text-slate-600">{subtitle}</p>
				</div>
				<button
					type="button"
					onClick={() => setOpen((prev) => !prev)}
					className="inline-flex items-center rounded-full border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
					aria-expanded={open}
				>
					{open ? <FiChevronUp /> : <FiChevronDown />}
				</button>
			</div>

			{open && (
				<div className="space-y-4">
					<label className="block space-y-1">
						<span className="text-xs font-semibold text-slate-500">Layout mode</span>
						<select
							disabled={!editable}
							className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
							value={modeValue}
							onChange={(e) => handleModeChange(e.target.value)}
						>
							{PAGE_LAYOUT_MODE_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
						<span className="block text-xs text-slate-500">
							{PAGE_LAYOUT_MODE_OPTIONS.find((opt) => opt.value === modeValue)?.description}
						</span>
					</label>

					<label className="block space-y-1">
						<span className="text-xs font-semibold text-slate-500">Fond de page</span>
						<select
							disabled={!editable}
							className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
							value={surfaceValue}
							onChange={(e) => handleSurfaceChange(e.target.value)}
						>
							{PAGE_LAYOUT_SURFACE_OPTIONS.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
						<span className="block text-xs text-slate-500">
							{PAGE_LAYOUT_SURFACE_OPTIONS.find((opt) => opt.value === surfaceValue)?.description}
						</span>
					</label>

					<div className="space-y-2">
						{PAGE_LAYOUT_FLAGS.map((flag) => (
							<label
								key={flag.key}
								className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5"
							>
								<input
									type="checkbox"
									className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400"
									disabled={!editable}
									checked={value?.[flag.key] === true}
									onChange={(e) => handleFlagChange(flag.key, e.target.checked)}
								/>
								<span className="min-w-0 flex-1">
									<span className="block text-xs font-semibold text-slate-700">{flag.label}</span>
									<span className="block text-xs text-slate-500">{flag.description}</span>
								</span>
							</label>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

/** Returns null when the layout object would be empty so we never persist
 *  `layout: {}` (cleaner payloads, smaller diffs in MinIO). */
function emptyToNull(layout: PageLayoutSettings): PageLayoutSettings | null {
	const hasMode = layout.mode === "fluid" || layout.mode === "constrained";
	const hasSurface = layout.surface === "white" || layout.surface === "tint";
	const hasFirst = layout.firstWidgetMobileFlush === true;
	const hasLast = layout.lastWidgetFlushBottom === true;
	return hasMode || hasSurface || hasFirst || hasLast ? layout : null;
}

export default PageEditorLayoutPanel;
